import React, { useCallback, useState, useEffect } from 'react';
import { Grid, TextField } from '@material-ui/core';
import { verifyDuplicateExamName, verifyDuplicateExamCode } from '../../services/validation/validation.service';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import AddSessionItemButton from './NewItemButton.component';
import { createExam, modifyExam } from '../../services/exams/sessions/sessions.service';
import { DetailsProps } from './DetailsProps.types';
import useExamSessionValidation from '../../hooks/useExamSessionValidation';
import { IExamDetails, IIdResult } from '../../models/session.types';
import { WorkMode, NewTreeItemId } from '../SessionTree/SessionTree.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import DeleteButton from './DeleteButton.component';
import { blockIconForInput } from './BlockItemForInput';

export interface ExamDetailsProps extends DetailsProps {
  data: IExamDetails;
  isReadOnly: () => boolean;
  isExamCodeReadOnly: () => boolean;
  sessionId: string;
  onDelete: () => void;
}

const ExamDetails: React.FC<ExamDetailsProps> = (props) => {
  const sessionDetailsClasses = sessionDetails();

  const [examName, setExamName] = useState<string>(props.data.name);
  const [examCode, setExamCode] = useState<string>(props.data.code);
  const [isFieldNameEdited, setIsFieldNameEdited] = useState<boolean>(false);
  const [isBlurName, setIsBlurName] = useState<boolean>(false);
  const [isFieldCodeEdited, setIsFieldCodeEdited] = useState<boolean>(false);
  const [isBlurCode, setIsBlurCode] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isValidExamSheetCode, setIsValidExamSheetCode] = useState<boolean>(true);
  const { openAlert } = useLayoutContext();
  const { isValidValue: isValidExamName } = useExamSessionValidation({
    isEdited: isFieldNameEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !examName ? examName : examName.trim(),
    data: !props.data.name ? props.data.name : props.data.name.trim(),
    verificationApi: useCallback((value: string) => verifyDuplicateExamName(props.sessionId, value), [props.sessionId]),
    onBlur: isBlurName,
  });

  const { isValidValue: isValidExamCode } = useExamSessionValidation({
    isEdited: isFieldCodeEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: !examCode ? examCode : examCode.trim(),
    data: !props.data.code ? props.data.code : props.data.code.trim(),
    verificationApi: useCallback((value: string) => verifyDuplicateExamCode(props.sessionId, value), [props.sessionId]),
    onBlur: isBlurCode,
  });

  const saveChanges = async () => {
    if (isValidExamName && isValidExamCode) {
      if (props.data.id !== NewTreeItemId.Exam) {
        await modifyExam(props.data.id, examName, examCode).then(
          () => {
            openAlert('success');
            setIsValidExamSheetCode(true);
            props.setWorkMode(WorkMode.VIEW);
            props.onSave({ id: props.data.id }, false);
          },
          (error) => {
            if (error.response.status === 400) {
              setIsValidExamSheetCode(false);
            }
            openAlert('error');
          },
        );
      } else {
        await createExam(props.data.parentId, examName, examCode).then(
          (result: AxiosResponse<IIdResult>) => {
            openAlert('success');
            setIsValidExamSheetCode(true);
            props.setWorkMode(WorkMode.VIEW);
            props.onSave(result.data, true);
          },
          (error) => {
            if (error.response.status === 400) {
              setIsValidExamSheetCode(false);
            }
            openAlert('error');
          },
        );
      }
    }
  };

  const revert = () => {
    setExamName(props.data.name);
    setExamCode(props.data.code);
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.SESSION, revert);
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setExamName(e.target.value.trimStart());
    setIsFieldNameEdited(true);
    setIsBlurName(false);
  };

  const handleChangeCode = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setExamCode(e.target.value.trimStart());
    setIsFieldCodeEdited(true);
    setIsBlurCode(false);
  };

  useEffect(() => {
    if (
      !isValidExamName ||
      !isValidExamCode ||
      (examName === props.data.name && examCode === props.data.code) ||
      !examName ||
      !examCode
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [isValidExamName, isValidExamCode, examName, props.data.name, props.data.code, examCode]);

  const editingDisabled = props.isReadOnly();
  const examCodeDisabled = props.isExamCodeReadOnly();
  const fieldsDisabled = props.workMode === WorkMode.VIEW || editingDisabled;

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton
                workMode={props.workMode}
                isDisabled={editingDisabled}
                handleEditMode={handleEditMode}
                isExamDetails={true}
              />
            ) : (
              <DeleteButton
                workMode={props.workMode}
                handleDeleteMode={props.onDelete}
                isDisabled={props.isReadOnly() || props.data.examSheets.length > 0}
              />
            )}
          </Grid>
          <Grid container justifyContent="center" alignContent="center">
            <Grid container justifyContent="center" className={sessionDetailsClasses.headerRow}>
              {props.workMode === WorkMode.VIEW && <div className={sessionDetailsClasses.headerText}>Egzamin</div>}
              {props.workMode === WorkMode.EDIT && (
                <div className={sessionDetailsClasses.headerText}>Edycja egzaminu</div>
              )}
              {props.workMode === WorkMode.ADD && (
                <div className={sessionDetailsClasses.headerText}>Zdefiniuj egzamin</div>
              )}
            </Grid>
            <Grid container justifyContent="center">
              <TextField
                className={sessionDetailsClasses.formInput}
                value={examName}
                onChange={handleChangeName}
                onBlur={() => {
                  setIsBlurName(true);
                }}
                id="outlined"
                label="Nazwa*"
                variant="outlined"
                disabled={fieldsDisabled}
                error={props.workMode !== WorkMode.VIEW && !isValidExamName}
                inputProps={{
                  'aria-label': 'Nazwa',
                  style: {
                    padding: '15.5px',
                    color: fieldsDisabled ? 'unset' : '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled),
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !isValidExamName
                    ? 'Pole "Nazwa" musi być unikatowe dla sesji egzaminacyjnej'
                    : undefined
                }
              />
              <TextField
                className={sessionDetailsClasses.formInput}
                value={examCode}
                onChange={handleChangeCode}
                onBlur={() => {
                  setIsBlurCode(true);
                }}
                id="outlined"
                label="Kod*"
                variant="outlined"
                disabled={fieldsDisabled || examCodeDisabled}
                error={(props.workMode !== WorkMode.VIEW && !isValidExamCode) || !isValidExamSheetCode}
                inputProps={{
                  'aria-label': 'Kod',
                  style: {
                    padding: '15.5px',
                    color: fieldsDisabled || examCodeDisabled ? 'unset' : '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled || examCodeDisabled),
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !isValidExamCode
                    ? 'Pole "Kod" musi być unikatowe dla sesji egzaminacyjnej'
                    : !isValidExamSheetCode
                    ? 'Kod arkusza musi być unikatowy w systemie'
                    : undefined
                }
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end">
            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons disable={isDisabled} saveHandle={saveChanges} closeHandle={closeHandle} />
            )}
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj arkusz"
                disabled={props.isSessionImported || editingDisabled}
                onClick={() => props.onAddChild(BreadcrumbLevel.EXAMSHEET, props.data.id)}
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ExamDetails;
